import React from "react";
import moment from "moment-timezone";
import { Box, Button, Paper, Stack, Text } from "@mantine/core";
import { IconBan, IconCalendarPlus } from "@tabler/icons-react";

interface SlotChooserProps {
  /** Pointer position of the slot click (the chooser anchors there). */
  position: { x: number; y: number };
  /** Clicked slot start (salon wall-clock as browser-local). */
  slotStart: Date | null;
  onAppointment: () => void;
  onTimeBlock: () => void;
  onDismiss: () => void;
}

const optionStyles = {
  root: { height: "auto", padding: "8px 9px", borderRadius: 7 },
  label: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 13.5,
    fontWeight: 500,
    color: "#1a1a1a",
  },
  section: { color: "#555555", marginRight: 9 },
};

/**
 * Empty-slot click: a small chooser (Appointment / Time block) anchored at
 * the click. A transparent full-screen layer dismisses it.
 */
const SlotChooser: React.FC<SlotChooserProps> = ({
  position,
  slotStart,
  onAppointment,
  onTimeBlock,
  onDismiss,
}) => (
  <>
    <Box
      onClick={onDismiss}
      style={{ position: "fixed", inset: 0, zIndex: 300 }}
    />
    <Paper
      radius={12}
      p={6}
      style={{
        position: "fixed",
        left: Math.min(position.x, window.innerWidth - 210),
        top: Math.min(position.y, window.innerHeight - 120),
        zIndex: 301,
        width: 200,
        boxSizing: "border-box",
        background: "#ffffff",
        border: "1px solid #e6e6e6",
        boxShadow: "0 10px 28px rgba(0,0,0,.14)",
      }}
    >
      <Stack gap={2}>
        {slotStart && (
          <Text
            style={{
              padding: "5px 8px 6px",
              fontFamily: "'Roboto Mono', monospace",
              fontSize: 11,
              color: "#8a8a8a",
            }}
          >
            {/* The slot Date already holds salon wall-clock as browser-local
                (that's how the grid places events), so format it as-is —
                re-applying .tz() would shift it. */}
            {moment(slotStart).format("ddd, MMM D · h:mm A")}
          </Text>
        )}
        <Button
          variant="subtle"
          color="gray"
          fullWidth
          justify="flex-start"
          leftSection={<IconCalendarPlus size={17} stroke={2} />}
          styles={optionStyles}
          onClick={onAppointment}
        >
          Appointment
        </Button>
        <Button
          variant="subtle"
          color="gray"
          fullWidth
          justify="flex-start"
          leftSection={<IconBan size={17} stroke={2} />}
          styles={optionStyles}
          onClick={onTimeBlock}
        >
          Time block
        </Button>
      </Stack>
    </Paper>
  </>
);

export default SlotChooser;
